import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { firstValueFrom } from 'rxjs';

import { IMPERSONATION_TICKET_QUERY_PARAM_KEY } from '../auth.config';

import {
  AuthorizationRequest,
  OAuthCallbackError,
  OAuthCallbackResult,
  OAuthTokens,
} from './oauth.model';
import { createCodeChallenge, randomState, randomUrlSafeToken } from './pkce';

/** Préfixe des clés de `sessionStorage`. Le `state` complète la clé, donc deux flux ne se marchent pas dessus. */
const STASH_PREFIX = 'rm-oauth-flow:';

interface StashedFlow {
  verifier: string;
  deepLink: string | null;
}

/**
 * Le flux `authorization_code` + PKCE, côté navigateur — la moitié qui manquait.
 *
 * <strong>Pourquoi il ne dépend pas d'`AUTH_LIB_CONFIG`.</strong> `rm-auth`, qui est le portail,
 * n'appelle `RmAuthModule.forRoot()` nulle part : le jeton d'injection y est donc absent. Un service
 * qui en dépendrait serait inutilisable là où on en a le plus besoin. Il prend ses paramètres
 * explicitement, ce qui vaut mieux de toute façon — rien de caché, et il se teste sans module.
 *
 * <strong>Pourquoi le lien profond passe par `sessionStorage` et non par une URL.</strong> En
 * `authorization_code`, la `redirect_uri` est fixe et sur liste blanche : le lien profond ne *peut*
 * plus être la cible de la redirection. Le ranger localement est meilleur que l'existant, où
 * `originUrl` et le jeton voyagent tous deux en query param — donc dans l'historique du navigateur,
 * les en-têtes `Referer` et les journaux CloudFront et ALB.
 */
@Injectable({ providedIn: 'root' })
export class OAuthFlowService {
  constructor(private http: HttpClient) {}

  /**
   * Amorce le flux : range le secret, puis quitte la page pour le serveur d'autorisation.
   *
   * `deepLink` est l'endroit où revenir après la connexion. Il est validé ici même : seul un chemin
   * relatif est retenu, pour que ce qui sera passé plus tard à une navigation ne puisse pas changer
   * d'hôte.
   */
  async start(
    request: AuthorizationRequest,
    deepLink?: string | null,
    loginHint?: string | null,
    impersonationTicket?: string | null
  ): Promise<void> {
    const verifier = randomUrlSafeToken();
    const state = randomState();
    const challenge = await createCodeChallenge(verifier);

    this.stash(state, { verifier, deepLink: safeDeepLink(deepLink) });
    window.location.assign(
      this.buildAuthorizationUrl(
        request,
        state,
        challenge,
        loginHint,
        impersonationTicket
      )
    );
  }

  /** Séparée d'`start()` pour être vérifiable sans quitter la page. */
  buildAuthorizationUrl(
    request: AuthorizationRequest,
    state: string,
    codeChallenge: string,
    loginHint?: string | null,
    impersonationTicket?: string | null
  ): string {
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: request.clientId,
      redirect_uri: request.redirectUri,
      scope: request.scope,
      state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });
    // `login_hint` est le paramètre OIDC standard qui dit *qui* va se connecter. Il ne remplace aucune
    // vérification : il ne fait que traverser le serveur d'autorisation jusqu'au formulaire, qui l'affiche
    // dans son champ e-mail. Sans lui, une application qui connaît déjà l'adresse de la personne la lui
    // redemande — c'est exactement ce que faisaient les fins de parcours du questionnaire.
    if (loginHint) {
      params.set('login_hint', loginHint);
    }
    // Le ticket d'usurpation (O2.18). Le PKCE reste celui de cette application : c'est elle qui range le
    // vérifieur, donc intercepter le ticket ne suffit pas — il faut aussi la session de l'employé.
    if (impersonationTicket) {
      params.set(IMPERSONATION_TICKET_QUERY_PARAM_KEY, impersonationTicket);
    }
    return `${request.issuer}/oauth2/authorize?${params.toString()}`;
  }

  /**
   * Traite le retour sur la `redirect_uri`.
   *
   * <strong>Un `state` que nous n'avons pas rangé est refusé, sans exception.</strong> C'est la
   * protection CSRF du flux : sans elle, un tiers pourrait faire consommer *son* code d'autorisation
   * par le navigateur de la victime, qui se retrouverait connectée sous le compte de l'attaquant. Le
   * refus n'est pas seulement prudent, il est aussi inévitable — sans le `code_verifier` rangé avec
   * ce `state`, l'échange échouerait de toute façon côté serveur.
   *
   * Refuser ne veut pas dire laisser une page blanche : l'appelant reçoit une `OAuthCallbackError`
   * typée et renvoie l'utilisateur vers sa page de connexion ou son accueil.
   */
  async consume(
    request: AuthorizationRequest,
    search: string
  ): Promise<OAuthCallbackResult> {
    const params = new URLSearchParams(search);

    const failure = params.get('error');
    if (failure) {
      throw new OAuthCallbackError(
        failure,
        params.get('error_description') ?? undefined
      );
    }

    const state = params.get('state');
    const code = params.get('code');
    const stashed = this.takeStash(state);

    if (!code || !stashed) {
      throw new OAuthCallbackError(
        'invalid_state',
        'Aucun flux en cours ne correspond à ce retour.'
      );
    }

    const tokens = await this.exchange(request, code, stashed.verifier);
    return { tokens, deepLink: stashed.deepLink };
  }

  /** L'échange du code contre les jetons, après validation du `state`. */
  private exchange(
    request: AuthorizationRequest,
    code: string,
    verifier: string
  ): Promise<OAuthTokens> {
    return this.postToken(request, {
      grant_type: 'authorization_code',
      code,
      redirect_uri: request.redirectUri,
      client_id: request.clientId,
      code_verifier: verifier,
    });
  }

  /**
   * Échange un jeton de rafraîchissement contre des jetons neufs.
   *
   * ⚠️ <strong>Un jeton de rafraîchissement ne doit être présenté qu'UNE FOIS.</strong> La rotation
   * est active, et depuis la story O2.9 le serveur <em>révoque toute la chaîne</em> quand il voit
   * rejouer un jeton déjà consommé — c'est ce qui révèle un vol, mais c'est aussi ce qui punit deux
   * onglets qui rafraîchiraient en même temps. La sérialisation n'est donc pas une optimisation :
   * c'est {@link OAuthSessionService} qui en a la charge, et ce service-ci ne doit jamais être appelé
   * directement pour rafraîchir.
   */
  refresh(
    request: AuthorizationRequest,
    refreshToken: string
  ): Promise<OAuthTokens> {
    return this.postToken(request, {
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: request.clientId,
    });
  }

  /**
   * Révoque un jeton — RFC 7009, le geste standard de déconnexion côté jetons.
   *
   * <strong>Pourquoi ça compte.</strong> Fermer la session du serveur d'autorisation ne révoque rien :
   * les deux mécanismes sont disjoints. Sans cet appel, un jeton de rafraîchissement exfiltré reste
   * valable **30 jours** après que l'utilisateur a cliqué « se déconnecter ». Effacer le stockage du
   * navigateur ne protège que l'utilisateur honnête.
   *
   * Révoquer le jeton de **rafraîchissement** suffit : le serveur invalide au passage le jeton d'accès
   * et le code d'autorisation de la même autorisation. Il ne touche pas au jeton d'identité, ce qui
   * tombe bien — c'est lui qui sert ensuite de `id_token_hint` à la déconnexion OIDC.
   *
   * Ne lève jamais : une déconnexion ne doit pas rester coincée parce que le serveur n'a pas répondu.
   * Le stockage local est effacé juste après, par l'appelant, quoi qu'il arrive.
   */
  async revoke(
    request: AuthorizationRequest,
    token: string,
    tokenTypeHint = 'refresh_token'
  ): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post(
          `${request.issuer}/oauth2/revoke`,
          new URLSearchParams({
            token,
            token_type_hint: tokenTypeHint,
            client_id: request.clientId,
          }).toString(),
          {
            headers: new HttpHeaders({
              'Content-Type': 'application/x-www-form-urlencoded',
            }),
            responseType: 'text',
          }
        )
      );
    } catch {
      // Refus, réseau coupé, serveur absent : on continue. Le jeton finira par expirer, et ne pas
      // pouvoir révoquer ne doit pas empêcher de se déconnecter.
    }
  }

  /**
   * Le seul appel à `/oauth2/token`, partagé par les deux grants.
   *
   * Écrit à la main parce que le client généré n'expose aucune opération `/oauth2/*`. Il est
   * **cross-origin** pour une application, et c'est mesuré comme autorisé : le serveur répond
   * `access-control-allow-origin: *` sur ce chemin. Ça marche précisément parce que cet appel n'a
   * besoin d'aucun credential — contrairement à l'ouverture de session, qui a exigé la même origine
   * et les behaviors CloudFront de la story O2.3.
   *
   * Aucun secret n'est envoyé : ce sont des clients **publics**, c'est PKCE qui les protège.
   */
  private postToken(
    request: AuthorizationRequest,
    fields: Record<string, string>
  ): Promise<OAuthTokens> {
    return firstValueFrom(
      this.http.post<OAuthTokens>(
        `${request.issuer}/oauth2/token`,
        new URLSearchParams(fields).toString(),
        {
          headers: new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded',
          }),
        }
      )
    );
  }

  private stash(state: string, flow: StashedFlow): void {
    try {
      sessionStorage.setItem(STASH_PREFIX + state, JSON.stringify(flow));
    } catch {
      // Stockage indisponible ou plein : le flux partira quand même, et c'est le retour qui sera
      // refusé faute de `state` connu. Mieux vaut ça qu'une exception au clic sur « se connecter ».
    }
  }

  /** Relit **et retire** : un `state` ne vaut qu'une fois, sinon un code rejoué reste consommable. */
  private takeStash(state: string | null): StashedFlow | null {
    if (!state) {
      return null;
    }
    const key = STASH_PREFIX + state;
    try {
      const raw = sessionStorage.getItem(key);
      sessionStorage.removeItem(key);
      return raw ? (JSON.parse(raw) as StashedFlow) : null;
    } catch {
      return null;
    }
  }
}

/**
 * Seul un chemin relatif est accepté comme lien profond. `//evil.com` est refusé autant que
 * `https://evil.com` : un chemin protocol-relative change d'hôte. Même forme que `safeResumeUri`
 * dans `rm-auth`, délibérément — deux validations d'URL qui divergent, c'est une de trop.
 */
export function safeDeepLink(deepLink?: string | null): string | null {
  if (!deepLink || !deepLink.startsWith('/') || deepLink.startsWith('//')) {
    return null;
  }
  return deepLink;
}
